import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Trophy, TrendingUp, TrendingDown, Users, Swords, Flame, Target } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import type { Activity, User } from "@shared/schema";

type ActivityWithUser = Activity & { user: User };

const activityConfig: Record<string, { icon: typeof Trophy; color: string; iconColor: string }> = {
  match_win: { icon: Trophy, color: "bg-victory/10 border-victory/20", iconColor: "text-victory" },
  match_loss: { icon: Swords, color: "bg-defeat/10 border-defeat/20", iconColor: "text-defeat" },
  rank_up: { icon: TrendingUp, color: "bg-primary/10 border-primary/20", iconColor: "text-primary" },
  rank_down: { icon: TrendingDown, color: "bg-muted border-muted-foreground/20", iconColor: "text-muted-foreground" },
  new_friend: { icon: Users, color: "bg-secondary/10 border-secondary/20", iconColor: "text-secondary" },
  league_join: { icon: Target, color: "bg-primary/10 border-primary/20", iconColor: "text-primary" },
  win_streak: { icon: Flame, color: "bg-victory/10 border-victory/20", iconColor: "text-victory" },
};

export function ActivityFeed() {
  const { data: activities, isLoading } = useQuery<ActivityWithUser[]>({
    queryKey: ['/api/activities/feed'],
  });

  const getTimeLabel = (timestamp: Date | string): string => {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    return formatDistanceToNow(date, { addSuffix: true, locale: es });
  };

  const initials = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.firstName?.[0]?.toUpperCase() || "?";
  };

  const getUserName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.firstName || "Jugador";
  };

  const getActivityValue = (activity: ActivityWithUser): string | undefined => {
    const metadata = activity.metadata as Record<string, unknown> | null;
    if (activity.activityType === "rank_up" || activity.activityType === "rank_down") {
      return metadata?.newLevel ? `${(metadata.newLevel as number).toFixed(2)}` : undefined;
    }
    if (activity.activityType === "win_streak") {
      return metadata?.streakCount?.toString();
    }
    return undefined;
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold px-1 text-muted-foreground uppercase tracking-wider">
          Actividad Reciente
        </h3>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border p-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold px-1 text-muted-foreground uppercase tracking-wider">
          Actividad Reciente
        </h3>
        <Card className="border p-6 text-center">
          <p className="text-sm text-muted-foreground">
            No hay actividad reciente de tus amigos y ligas
          </p>
        </Card>
      </div>
    );
  }

  const displayActivities = activities.slice(0, 7);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold px-1 text-muted-foreground uppercase tracking-wider">
        Actividad Reciente
      </h3>
      <div className="space-y-2">
        {displayActivities.map((activity) => {
          const config = activityConfig[activity.activityType] || activityConfig.match_win;
          const { icon: Icon, color, iconColor } = config;
          const value = getActivityValue(activity);

          return (
            <Card
              key={activity.id}
              className={cn("border transition-all hover-elevate p-3", color)}
              data-testid={`row-activity-${activity.id}`}
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9 flex-shrink-0">
                  <AvatarImage src={activity.user.profileImageUrl || undefined} alt={getUserName(activity.user)} />
                  <AvatarFallback className="text-xs">{initials(activity.user)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="font-semibold text-foreground">{getUserName(activity.user)}</span>{" "}
                    <span className="text-muted-foreground">{activity.description}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{activity.createdAt ? getTimeLabel(activity.createdAt) : ''}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {value !== undefined && (
                    <span className="text-sm font-bold text-foreground">{value}</span>
                  )}
                  <div className={cn("p-2 rounded-full", iconColor)}>
                    <Icon className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
